<h2 mat-dialog-title>{{ dialogTitle }}</h2>

<mat-dialog-content class="mat-typography">
    <p>Fill in the client's details. We'll send them a pre-filled application link.</p>

    <form
        *ngIf="applicationLinkForm"
        [formGroup]="applicationLinkForm"
        (ngSubmit)="onSubmit()"
        novalidate
        #ngForm="ngForm"
    >
        <div fxLayout="column" fxLayoutGap="15px">
            <!-- Client Name -->
            <fieldset formGroupName="clientName" class="fieldset-group">
                <legend>Client Name</legend>
                <div fxLayout="row wrap" fxLayoutGap="15px grid">
                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(33.33% - 10px)">
                        <mat-label>First Name</mat-label>
                        <input matInput formControlName="firstName" autocomplete="given-name" required />
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('firstName')?.touched &&
                                clientNameForm?.get('firstName')?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(33.33% - 10px)">
                        <mat-label>Middle Name (Optional)</mat-label>
                        <input matInput formControlName="middleName" autocomplete="additional-name" />
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(33.33% - 10px)">
                        <mat-label>Last Name</mat-label>
                        <input matInput formControlName="lastName" autocomplete="family-name" required />
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('lastName')?.touched &&
                                clientNameForm?.get('lastName')?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                    </mat-form-field>
                </div>
            </fieldset>

            <!-- Contact Details -->
            <fieldset class="fieldset-group">
                <legend>Contact Details</legend>
                <div fxLayout="row wrap" fxLayoutGap="15px grid">
                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Email</mat-label>
                        <input
                            matInput
                            type="email"
                            formControlName="clientEmail"
                            placeholder="client@example.com"
                            autocomplete="email"
                            required
                        />
                        <mat-error
                            *ngIf="
                                formControls?.clientEmail?.touched && formControls?.clientEmail?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                        <mat-error
                            *ngIf="formControls?.clientEmail?.touched && formControls?.clientEmail?.hasError('email')"
                        >
                            Enter a valid email address.
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Mobile Number</mat-label>
                        <input
                            matInput
                            type="tel"
                            formControlName="clientPhoneNumber"
                            placeholder="0412 345 678"
                            autocomplete="tel"
                            required
                        />
                        <mat-error
                            *ngIf="
                                formControls?.clientPhoneNumber?.touched &&
                                formControls?.clientPhoneNumber?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.clientPhoneNumber?.touched &&
                                formControls?.clientPhoneNumber?.hasError('pattern')
                            "
                        >
                            Enter a valid Australian mobile number.
                        </mat-error>
                    </mat-form-field>
                </div>
            </fieldset>

            <!-- Residential Address -->
            <fieldset class="fieldset-group">
                <legend>Residential Address</legend>
                <bp-address
                    formGroupName="clientResidentialAddress"
                    [states]="addressStatesForBpAddress"
                    data-test-id="client-residential-address"
                ></bp-address>

                <ng-container *ngIf="clientAddressForm?.touched || clientAddressForm?.dirty">
                    <mat-error
                        *ngIf="clientAddressForm?.get('fullAddress')?.hasError('selectedAddressRequired')"
                        data-test-id="fullAddress-selected-error"
                    >
                        Select an address from the suggestions, or complete the fields below manually.
                    </mat-error>
                    <mat-error
                        *ngIf="clientAddressForm?.get('fullAddress')?.hasError('_internalError')"
                        data-test-id="fullAddress-internal-error"
                    >
                        Address search failed. Please enter the address manually.
                    </mat-error>
                    <mat-error
                        *ngIf="
                            clientAddressForm?.hasError('addressIncomplete') ||
                            clientAddressForm?.hasError('addressSelectionOrManualRequired')
                        "
                        data-test-id="address-incomplete-error"
                    >
                        Please complete all address fields.
                    </mat-error>
                    <mat-error
                        *ngIf="
                            clientAddressForm?.get('state')?.hasError('heslpNswOnly') &&
                            clientAddressForm?.get('state')?.touched
                        "
                        data-test-id="heslp-nsw-only-error"
                    >
                        HESLP is only available for NSW addresses.
                    </mat-error>
                </ng-container>
            </fieldset>

            <!-- System Information -->
            <fieldset class="fieldset-group">
                <legend>System Information</legend>

                <mat-form-field appearance="outline" fxFlex="100%">
                    <mat-label>System Cost ($)</mat-label>
                    <input
                        matInput
                        type="text"
                        formControlName="systemCost"
                        bpCurrencyFormat
                        placeholder="e.g., 10,000"
                        autocomplete="off"
                        required
                    />
                    <mat-hint *ngIf="dialogInputData.isSystemCostDisabled">
                        Auto-filled from your quote — not editable.
                    </mat-hint>
                    <mat-error
                        *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('required')"
                    >
                        Required.
                    </mat-error>
                    <mat-error *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('min')">
                        Must be greater than $1,000.
                    </mat-error>
                    <mat-error
                        *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('pattern')"
                    >
                        Enter a whole dollar amount (no cents).
                    </mat-error>
                </mat-form-field>

                <p *ngIf="heslpLoanWarningAmount > 0" class="heslp-loan-warning">
                    The maximum HESLP loan is $15,000. Your client will need to cover the remaining
                    ${{ heslpLoanWarningAmount | number:'1.0-0' }} as a deposit.
                </p>

                <p *ngIf="interestFreeLoanWarningAmount > 0" class="heslp-loan-warning">
                    The maximum Zero interest payment plan loan is $45,000. Your client will need to cover the remaining
                    ${{ interestFreeLoanWarningAmount | number:'1.0-0' }} as a deposit.
                </p>

                <bp-renewable-system-types
                    formControlName="systemType"
                    [productTag]="dialogInputData.productTag"
                    [allowedSystemTypes]="allowedSystemTypes"
                    data-test-id="interest-free-system-types"
                    required
                ></bp-renewable-system-types>
                <mat-error *ngIf="formControls?.systemType?.touched && formControls?.systemType?.hasError('required')">
                    Select at least one system type.
                </mat-error>
                <mat-error
                    *ngIf="
                        formControls?.systemType?.touched &&
                        formControls?.systemType?.hasError('heslpUnsupportedSystemTypes')
                    "
                >
                    HESLP only allows Solar and/or Battery installations.
                </mat-error>

                <div *ngIf="isHeslp" class="replaces-equipment-field" fxLayout="column" fxLayoutGap="8px">
                    <div
                        fxLayout.gt-xs="row"
                        fxLayoutGap.gt-xs="16px"
                        fxLayout.xs="column"
                        fxLayoutGap.xs="8px"
                        fxLayoutAlign.gt-xs=" center"
                    >
                        <label id="replaces-equipment-label" class="replaces-equipment-label" fxFlex>
                            This installation replaces existing equipment
                        </label>
                        <mat-button-toggle-group
                            formControlName="isReplacingEquipment"
                            aria-labelledby="replaces-equipment-label"
                            data-test-id="is-replacing-equipment"
                        >
                            <mat-button-toggle [value]="true">Yes</mat-button-toggle>
                            <mat-button-toggle [value]="false">No</mat-button-toggle>
                        </mat-button-toggle-group>
                    </div>
                    <mat-error
                        *ngIf="
                            formControls?.isReplacingEquipment?.touched &&
                            formControls?.isReplacingEquipment?.hasError('required')
                        "
                    >
                        Required.
                    </mat-error>
                </div>

                <div *ngIf="shouldShowSolarPanelSize()" class="system-size-field">
                    <mat-form-field appearance="outline" fxFlex="100%">
                        <mat-label>Solar Panel Size (kW)</mat-label>
                        <input
                            matInput
                            type="number"
                            formControlName="solarPanelSize"
                            placeholder="e.g., 6.6"
                            autocomplete="off"
                            step="0.1"
                            min="0"
                            required
                            (wheel)="$event.preventDefault()"
                        />
                        <mat-error
                            *ngIf="
                                formControls?.solarPanelSize?.touched &&
                                formControls?.solarPanelSize?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.solarPanelSize?.touched && formControls?.solarPanelSize?.hasError('min')
                            "
                        >
                            Must be greater than 0.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.solarPanelSize?.touched &&
                                formControls?.solarPanelSize?.hasError('pattern')
                            "
                        >
                            Enter a valid size (e.g., 6.6).
                        </mat-error>
                    </mat-form-field>
                </div>

                <div *ngIf="shouldShowBatterySize()" class="system-size-field">
                    <mat-form-field appearance="outline" fxFlex="100%">
                        <mat-label>Battery Size (kWh)</mat-label>
                        <input
                            matInput
                            type="number"
                            formControlName="batterySize"
                            placeholder="e.g., 13.5"
                            autocomplete="off"
                            step="0.1"
                            min="0"
                            required
                            (wheel)="$event.preventDefault()"
                        />
                        <mat-error
                            *ngIf="
                                formControls?.batterySize?.touched && formControls?.batterySize?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                        <mat-error
                            *ngIf="formControls?.batterySize?.touched && formControls?.batterySize?.hasError('min')"
                        >
                            {{
                                isHeslp
                                    ? 'HESLP requires batteries of at least ' + heslpMinimumBatteryCapacityKwh + ' kWh.'
                                    : 'Must be greater than 0.'
                            }}
                        </mat-error>
                        <mat-error
                            *ngIf="formControls?.batterySize?.touched && formControls?.batterySize?.hasError('pattern')"
                        >
                            Enter a valid size (e.g., 13.5).
                        </mat-error>
                    </mat-form-field>
                </div>
            </fieldset>

            <!-- Electricity Bill Savings -->
            <fieldset *ngIf="!isAcOnly" formGroupName="electricityBillSavings" class="fieldset-group electricity-savings">
                <legend>Estimated Electricity Bill Savings</legend>
                <div fxLayout="row wrap" fxLayoutGap="15px grid">
                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Amount ($)</mat-label>
                        <input
                            matInput
                            type="text"
                            bpCurrencyFormat
                            [allowDecimals]="true"
                            [maxDecimalPlaces]="2"
                            formControlName="amount"
                            placeholder="e.g., 250"
                            autocomplete="off"
                            required
                        />
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('amount')?.touched &&
                                electricityBillSavingsForm?.get('amount')?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('amount')?.touched &&
                                electricityBillSavingsForm?.get('amount')?.hasError('min')
                            "
                        >
                            Must be $0 or more.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('amount')?.touched &&
                                electricityBillSavingsForm?.get('amount')?.hasError('pattern')
                            "
                        >
                            Enter a valid amount (e.g., 250.50).
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Frequency</mat-label>
                        <mat-select formControlName="frequency" required>
                            <mat-option *ngFor="let freq of sortedSavingFrequencies" [value]="freq.value">
                                {{ freq.value }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('frequency')?.touched &&
                                electricityBillSavingsForm?.get('frequency')?.hasError('required')
                            "
                        >
                            Required.
                        </mat-error>
                    </mat-form-field>
                </div>

                <mat-checkbox formControlName="pricingExplanationConfirmation" class="confirmation-checkbox">
                    I've explained the estimated electricity savings to the client based on reasonable assumptions.
                </mat-checkbox>
                <mat-error
                    *ngIf="
                        electricityBillSavingsForm?.get('pricingExplanationConfirmation')?.touched &&
                        electricityBillSavingsForm?.get('pricingExplanationConfirmation')?.hasError('required')
                    "
                    data-test-id="pricing-explanation-error"
                >
                    Please check this box before sending.
                </mat-error>
            </fieldset>

            <!-- Income Eligibility (HESLP / WABS) -->
            <fieldset class="fieldset-group" *ngIf="applicationLinkForm?.get('totalIncomeLessThanThresholdConfirmed')">
                <legend>{{ isHeslp ? 'Eligibility & Disclosure' : 'Income Eligibility' }}</legend>
                <mat-checkbox formControlName="totalIncomeLessThanThresholdConfirmed" class="confirmation-checkbox">
                    <ng-container *ngIf="isHeslp; else defaultIncomeText">
                        I confirm my client's household taxable income does not exceed $210,000 and I have quoted my client including all eligible rebates and incentives available to them at federal and state level.
                    </ng-container>
                    <ng-template #defaultIncomeText>
                        I confirm my client's household income is under $210,000.
                    </ng-template>
                </mat-checkbox>
                <mat-error
                    *ngIf="
                        (ngForm?.submitted ||
                            applicationLinkForm?.get('totalIncomeLessThanThresholdConfirmed')?.touched) &&
                        applicationLinkForm?.get('totalIncomeLessThanThresholdConfirmed')?.hasError('required')
                    "
                    data-test-id="totalIncomeLessThanThresholdConfirmed-error"
                >
                    Please check this box before sending.
                </mat-error>
            </fieldset>
        </div>
    </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-stroked-button type="button" (click)="onCancel()" [disabled]="isLoading" data-test-id="cancel-button">
        Cancel
    </button>
    <button
        mat-raised-button
        color="primary"
        type="submit"
        (click)="onSubmit()"
        [disabled]="isLoading || (applicationLinkForm && applicationLinkForm.invalid)"
        data-test-id="submit-button"
    >
        <ng-container *ngIf="!isLoading; else loadingContent">Send Link</ng-container>
        <ng-template #loadingContent>
            <span class="btn-loading">
                <mat-spinner [diameter]="20" [strokeWidth]="2"></mat-spinner>
                <span>Sending...</span>
            </span>
        </ng-template>
    </button>
</mat-dialog-actions>
